import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import type {
  AvailableUnitsResponse,
  UnitDetailsResponse,
} from '@/core/api/models/properties';
import { logger } from '@/core/logger';

export interface GetAvailableUnitsParams {
  projectId?: string;
  /** Max total_price (AED). Omitted when not filtering. */
  maxPrice?: number;
  /** Pagination starts at 0 */
  page?: number;
  limit?: number;
}

/**
 * Shared units / ADHA profile API (web + native).
 * Bearer JWT via apiClient — customer resolved from login token.
 * project_id is not sent; the token identifies the catalogue.
 */
export class UnitsService {
  async getAvailableUnits(
    projectIdOrParams?: string | GetAvailableUnitsParams,
    maxPrice?: number,
  ): Promise<AvailableUnitsResponse> {
    let params: GetAvailableUnitsParams;

    if (typeof projectIdOrParams === 'string' || projectIdOrParams === undefined) {
      params = {
        page: 0,
        limit: 20,
      };
      if (maxPrice != null) {
        params.maxPrice = maxPrice;
      }
    } else {
      params = {
        page: projectIdOrParams.page ?? 0,
        limit: projectIdOrParams.limit ?? 20,
      };
      if (projectIdOrParams.maxPrice != null) {
        params.maxPrice = projectIdOrParams.maxPrice;
      }
    }

    logger.info('UnitsService:Fetching available units', params);

    try {
      return await apiClient.get<AvailableUnitsResponse>(Endpoints.property.units, {
        params: {
          page: params.page ?? 0,
          limit: params.limit ?? 20,
          ...(params.maxPrice != null && params.maxPrice > 0
            ? { max_price: params.maxPrice }
            : {}),
        },
      });
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: string }).message || '')
          : '';
      // Backend returns 400 when the project has no units — treat as empty list.
      if (/no units/i.test(message)) {
        logger.info('UnitsService:No units found — returning empty list');
        return {
          status: 'success',
          message: 'No units Found.',
          page: params.page ?? 0,
          limit: params.limit ?? 20,
          hasNext: false,
          units: [],
        };
      }
      throw error;
    }
  }

  /**
   * GET /v1/customers/profile
   * ADHA catalogue profile + project context. Bearer JWT only — no ID query.
   */
  async getUnitDetails(): Promise<UnitDetailsResponse> {
    logger.info('UnitsService:Fetching ADHA customer profile');

    return apiClient.get<UnitDetailsResponse>(Endpoints.property.unitDetails);
  }
}

export const unitsService = new UnitsService();
